import { useState } from 'react';
import {
  AlertTriangle, Search, Filter, Plus, Calendar, Clock, CheckCircle2,
  ChevronRight, User, ArrowRight, AlertCircle, Shield, Eye, XCircle,
  CheckCircle, RotateCcw
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { formatDate, getCAPAStatusLabel, getCAPAStatusColor, getCAPAPriorityColor, getCAPAStatusStep } from '../../utils/helpers';

const CAPA_WORKFLOW = ['open', 'assigned', 'in_progress', 'pending_review', 'closed', 'verified'];

export default function CAPAPage() {
  const { correctiveActions, updateCAPAStatus } = useAppStore();
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedCAPA, setSelectedCAPA] = useState<string | null>(null);

  const filtered = correctiveActions.filter(c => {
    if (search && !c.vesselName.toLowerCase().includes(search.toLowerCase()) && !c.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    return true;
  });

  const detail = correctiveActions.find(c => c.id === selectedCAPA);
  const overdueCount = correctiveActions.filter(c => c.isOverdue).length;
  const openCount = correctiveActions.filter(c => c.status === 'open' || c.status === 'assigned').length;
  const inProgressCount = correctiveActions.filter(c => c.status === 'in_progress').length;
  const closedCount = correctiveActions.filter(c => c.status === 'closed' || c.status === 'verified').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Corrective Actions</h1>
          <p className="text-sm text-slate-500 mt-1">Track and manage CAPA items from inspection findings</p>
        </div>
        <button className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 flex items-center gap-2 transition-colors shadow-md shadow-sky-500/20">
          <Plus className="w-4 h-4" /> New CAPA
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Overdue', value: overdueCount, color: 'bg-red-50 text-red-600 border-red-100', icon: AlertCircle },
          { label: 'Open / Assigned', value: openCount, color: 'bg-amber-50 text-amber-600 border-amber-100', icon: Clock },
          { label: 'In Progress', value: inProgressCount, color: 'bg-blue-50 text-blue-600 border-blue-100', icon: RotateCcw },
          { label: 'Closed / Verified', value: closedCount, color: 'bg-emerald-50 text-emerald-600 border-emerald-100', icon: CheckCircle },
        ].map(kpi => (
          <div key={kpi.label} className={`rounded-2xl p-4 border ${kpi.color}`}>
            <div className="flex items-center gap-3">
              <kpi.icon className="w-8 h-8 opacity-60" />
              <div>
                <p className="text-3xl font-bold">{kpi.value}</p>
                <p className="text-xs font-medium opacity-70">{kpi.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search corrective actions..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'open', 'in_progress', 'pending_review', 'closed', 'verified'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                  statusFilter === s ? 'bg-[#0A1628] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}>
                {s === 'all' ? 'All' : getCAPAStatusLabel(s as any)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* CAPA List */}
        <div className="lg:col-span-2 space-y-3">
          {filtered.map(capa => (
            <div key={capa.id}
              onClick={() => setSelectedCAPA(selectedCAPA === capa.id ? null : capa.id)}
              className={`bg-white rounded-2xl border p-5 cursor-pointer transition-all hover:shadow-lg group ${
                selectedCAPA === capa.id ? 'border-sky-500 shadow-lg' : 'border-slate-100'
              }`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getCAPAPriorityColor(capa.priority)}`}>
                      {capa.priority}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCAPAStatusColor(capa.status)}`}>
                      {getCAPAStatusLabel(capa.status)}
                    </span>
                    {capa.isOverdue && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">OVERDUE</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-slate-800 group-hover:text-sky-600 transition-colors">{capa.title}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">{capa.vesselName} · Due: {formatDate(capa.dueDate)}</p>
                </div>
                {capa.daysRemaining > 0 && (
                  <div className="text-right flex-shrink-0 ml-3">
                    <p className={`text-lg font-bold ${capa.daysRemaining < 14 ? 'text-red-600' : capa.daysRemaining < 30 ? 'text-amber-600' : 'text-slate-600'}`}>
                      {capa.daysRemaining}
                    </p>
                    <p className="text-[10px] text-slate-400">days left</p>
                  </div>
                )}
              </div>

              {/* Workflow Progress */}
              <div className="flex items-center gap-1 mt-3">
                {CAPA_WORKFLOW.map((step, i) => (
                  <div key={step} className="flex-1">
                    <div className={`h-1.5 rounded-full ${
                      i <= getCAPAStatusStep(capa.status) ? 'bg-sky-500' : 'bg-slate-100'
                    }`} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-1">
                {CAPA_WORKFLOW.map(step => (
                  <span key={step} className="text-[8px] text-slate-400 capitalize">{step.replace('_', ' ')}</span>
                ))}
              </div>

              {/* Expanded */}
              {selectedCAPA === capa.id && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                  <p className="text-sm text-slate-600">{capa.description}</p>
                  {capa.rootCause && (
                    <div className="p-3 bg-amber-50 rounded-xl">
                      <p className="text-xs font-medium text-amber-700 mb-1">Root Cause</p>
                      <p className="text-sm text-amber-800">{capa.rootCause}</p>
                    </div>
                  )}
                  {capa.actionTaken && (
                    <div className="p-3 bg-emerald-50 rounded-xl">
                      <p className="text-xs font-medium text-emerald-700 mb-1">Action Taken</p>
                      <p className="text-sm text-emerald-800">{capa.actionTaken}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-slate-400">Owner</p>
                      <p className="font-medium text-slate-700">{capa.owner}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Due Date</p>
                      <p className="font-medium text-slate-700">{formatDate(capa.dueDate)}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {capa.status !== 'verified' && (
                      <button onClick={(e) => {
                        e.stopPropagation();
                        const nextIdx = CAPA_WORKFLOW.indexOf(capa.status) + 1;
                        if (nextIdx < CAPA_WORKFLOW.length) updateCAPAStatus(capa.id, CAPA_WORKFLOW[nextIdx] as any);
                      }} className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors flex items-center gap-2">
                        <ArrowRight className="w-4 h-4" /> Advance Status
                      </button>
                    )}
                    <button className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors">
                      View Observation
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* CAPA Summary */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <h3 className="font-semibold text-slate-800 mb-4">CAPA Summary</h3>
            <div className="space-y-3">
              {correctiveActions.map(capa => (
                <div key={capa.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                    capa.isOverdue ? 'bg-red-500' :
                    capa.status === 'closed' || capa.status === 'verified' ? 'bg-emerald-500' :
                    'bg-sky-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{capa.vesselName}</p>
                    <p className="text-[10px] text-slate-400 truncate">{capa.title}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${getCAPAStatusColor(capa.status)}`}>
                    {getCAPAStatusLabel(capa.status)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
